import * as StateMachine from 'javascript-state-machine';

import {HeartbeatService} from '../../core/HeartbeatService';
import {Event, JSM_INITIAL_EVENT} from '../../enums/Event';
import {createVideoStartFailedReason} from '../../enums/VideoStartFailedReason';
import {AnalyticsEventBase, ErrorEvent} from '../../types/EventData';
import type {StateMachineCallbacks} from '../../types/StateMachineCallbacks';
import {EventDebugging} from '../../utils/EventDebugging';
import {logger} from '../../utils/Logger';
import {isFunction, isNotNullish, noOp} from '../../utils/Utils';
import {AnalyticsStateMachine} from '../AnalyticsStateMachine';
import {
  customStateMachineErrorCallback,
  logMissingCallbackWarning,
  logStateMachineTransition,
  on,
  whileIn,
} from '../stateMachineUtils';

export enum State {
  AD_PAUSE = 'AD_PAUSE',
  AD_PLAYING = 'AD_PLAYING',
  AD_READY = 'AD_READY',
  /** This event is called when an ad is played directly after the player startup phase */
  AD_STARTUP = 'AD_STARTUP',
  AUDIOTRACK_CHANGING = 'AUDIOTRACK_CHANGING',
  BUFFERING = 'BUFFERING',
  CASTING = 'CASTING',
  /**
   * This state was created only to be able to "cut" the {@link State.PLAYING} and {@link State.PAUSE}
   * and send playing/pause sample with current custom data, while calling {@link AdapterAPI.setCustomData} function
   * and setting the new custom data values.
   *
   * @remarks
   *
   * Our implementation relays on a specific state name "CUSTOMDATACHANGE" because it should call the
   * default "onleavestate" state machine callback {@link stateMachineCallbacks.customdatachange}.
   */
  CUSTOMDATACHANGE = 'CUSTOMDATACHANGE',
  END = 'END',
  /** see {@link State.PLAY_SEEKING} */
  END_PLAY_SEEKING = 'END_PLAY_SEEKING',
  ERROR = 'ERROR',
  INITIAL_SOURCE_CHANGE = 'INITIAL_SOURCE_CHANGE',
  MUTING_PAUSE = 'MUTING_PAUSE',
  MUTING_PLAY = 'MUTING_PLAY',
  MUTING_READY = 'MUTING_READY',
  PAUSE = 'PAUSE',
  /**
   * THE Seeking state.
   * Seeking tracking = time spend in PLAY_SEEKING + END_PLAY_SEEKING.
   * - PLAY_SEEKING - THE seeking state for tracking time spend in seeking.
   * - END_PLAY_SEEKING - part of seeking time, where we wait `in between` seeking and continuing with other states.
   *  - usually we are in this state when holding the mouse while seeking -> this will produce multiple connected seeks.
   */
  PLAY_SEEKING = 'PLAY_SEEKING',
  PLAYING = 'PLAYING',
  QUALITY_CHANGE = 'QUALITY_CHANGE',
  /**
   * Being in the READY state means that a source was loaded successfully
   */
  READY = 'READY',
  SETUP = 'SETUP',
  SOURCE_CHANGING = 'SOURCE_CHANGING',
  STARTUP = 'STARTUP',
  /**
   * Special state for tracking startup when autoplay is enabled.
   *
   * A dedicated state is required because the normal STARTUP state has custom transitions that would compromise
   * the semantic meaning of the READY state (which should indicate "source successfully loaded").
   *
   * Problem: In normal startup, a PAUSE event during startup transitions back to READY.
   * If we reused STARTUP for autoplay, this would break the invariant that READY always means
   * "source was successfully loaded" - because autoplay startup can PAUSE before the source is loaded.
   *
   * Solution: STARTUP_AUTOPLAY maintains separate transition logic and tracking of whether
   * the source has been loaded, allowing correct startup time calculation and state semantics.
   */
  STARTUP_AUTOPLAY = 'STARTUP_AUTOPLAY',
  SUBTITLE_CHANGING = 'SUBTITLE_CHANGING',
  VIDEOSTART_FAILED = 'VIDEOSTART_FAILED',
}

// Custom internal state machine events
enum CustomEvents {
  PAUSE_AFTER_SOURCE_LOADED = 'PAUSE_AFTER_SOURCE_LOADED',
  PAUSE_BEFORE_SOURCE_LOADED = 'PAUSE_BEFORE_SOURCE_LOADED',
}

export class Bitmovin8AnalyticsStateMachine extends AnalyticsStateMachine {
  private debuggingStates: EventDebugging[] = [];
  private enabledDebugging = false;

  private seekStartTimestamp: number | undefined;
  /**
   * Used for ads, because when AdFinished event is fired and a call to state machine is made,
   * data from player indicate that video is playing from beginning even if ad is in the middle of video
   */
  private lastTrackedVideoEndObject?: AnalyticsEventBase;

  /**
   * Tracks whether a source was successfully loaded during the STARTUP_AUTOPLAY state.
   *
   * This flag is critical for handling two edge cases during autoplay startup:
   *
   * **Case 1: SOURCE_UNLOADED event handling**
   * In autoplay, the transition to STARTUP_AUTOPLAY happens immediately after player.load() is called.
   * During a source change, SOURCE_UNLOADED may be fired to clean up the previous source,
   * but this can occur BEFORE SOURCE_LOADED for the new source and AFTER the transition to STARTUP_AUTOPLAY.
   *
   * - If flag=false (no source loaded yet): SOURCE_UNLOADED is cleanup of old source → ignore
   *   Example: PLAYING → SOURCE_CHANGING → LOADSTART_AUTOPLAY → STARTUP_AUTOPLAY → SOURCE_UNLOADED (ignored)
   *
   * - If flag=true (source is loaded): SOURCE_UNLOADED is an error condition → trigger VIDEOSTART_FAILED
   *   Example: PLAYING → SOURCE_CHANGING → LOADSTART_AUTOPLAY → STARTUP_AUTOPLAY → SOURCE_LOADED → SOURCE_UNLOADED (error)
   *
   * **Case 2: PAUSE event handling**
   * In normal startup, PAUSE returns to READY state (safe because source is loaded).
   * In autoplay startup, we must transition based on whether source is loaded:
   *
   * - If flag=false (no source loaded yet): PAUSE → INITIAL_SOURCE_CHANGE (no sample)
   *   Example: SETUP → STARTUP_AUTOPLAY → PAUSE → INITIAL_SOURCE_CHANGE
   *
   * - If flag=true (source is loaded): PAUSE → READY (no sample, source is loaded)
   *   Example: SETUP → STARTUP_AUTOPLAY → SOURCE_LOADED → PAUSE → READY
   */
  private sourceLoadedDuringAutoplayStartup = false;

  constructor(stateMachineCallbacks: StateMachineCallbacks, playingHeartbeatService: HeartbeatService) {
    super(stateMachineCallbacks, playingHeartbeatService);
  }

  getAllStatesBut(states: string[]) {
    return this.getAllStates().filter((i) => states.indexOf(i) < 0);
  }

  getAllStates() {
    return Object.keys(State).map((key) => State[key]);
  }

  override createStateMachine(): StateMachine.StateMachine {
    return StateMachine.create({
      // custom initial state and custom JSM initial event name
      // initial event is used by JSM for the initial state transition 'none' -> 'SETUP'
      initial: {state: State.SETUP, event: JSM_INITIAL_EVENT},
      error: customStateMachineErrorCallback,
      events: [
        {
          name: Event.SOURCE_LOADED,
          from: [State.SETUP, State.ERROR, State.SOURCE_CHANGING, State.INITIAL_SOURCE_CHANGE],
          to: State.READY,
        },

        {name: Event.PLAY, from: State.READY, to: State.STARTUP},
        {name: Event.PLAYING, from: State.READY, to: State.PLAYING},
        on(Event.READY).stayIn(State.READY),

        //#region VIDEOSTART_FAILED
        {
          name: Event.ERROR,
          from: [State.STARTUP, State.STARTUP_AUTOPLAY],
          to: State.VIDEOSTART_FAILED,
        },
        {
          name: Event.UNLOAD,
          from: [State.STARTUP, State.STARTUP_AUTOPLAY],
          to: State.VIDEOSTART_FAILED,
        },
        {name: Event.VIDEOSTART_TIMEOUT, from: [State.STARTUP, State.STARTUP_AUTOPLAY], to: State.VIDEOSTART_FAILED},
        {name: Event.SOURCE_UNLOADED, from: [State.STARTUP, State.STARTUP_AUTOPLAY], to: State.VIDEOSTART_FAILED},
        {name: Event.MANUAL_SOURCE_CHANGE, from: [State.STARTUP, State.STARTUP_AUTOPLAY], to: State.VIDEOSTART_FAILED},
        //#endregion VIDEOSTART_FAILED

        //#region STARTUP and STARTUP_AUTOPLAY
        {name: Event.TIMECHANGED, from: [State.STARTUP, State.STARTUP_AUTOPLAY], to: State.PLAYING},
        on(Event.PLAY).stayIn(State.STARTUP),
        on(Event.PLAYING).stayIn(State.STARTUP),
        on(Event.START_BUFFERING).stayIn(State.STARTUP),
        on(Event.END_BUFFERING).stayIn(State.STARTUP),
        on(Event.VIDEO_CHANGE).stayIn(State.STARTUP),
        on(Event.AUDIO_CHANGE).stayIn(State.STARTUP),
        on(Event.READY).stayIn(State.STARTUP),
        {name: Event.PAUSE, from: State.STARTUP, to: State.READY},

        // ready state transition is not needed because the player.load() will always happen before the SOURCE_LOADED event
        {
          name: Event.LOADSTART_AUTOPLAY,
          from: [State.SETUP, State.SOURCE_CHANGING, State.INITIAL_SOURCE_CHANGE],
          to: State.STARTUP_AUTOPLAY,
        },
        {name: CustomEvents.PAUSE_BEFORE_SOURCE_LOADED, from: State.STARTUP_AUTOPLAY, to: State.INITIAL_SOURCE_CHANGE},
        {name: CustomEvents.PAUSE_AFTER_SOURCE_LOADED, from: State.STARTUP_AUTOPLAY, to: State.READY},
        ...whileIn(State.STARTUP_AUTOPLAY).passOver(
          // Custom behaviour in the onbeforePause callback
          Event.PAUSE,
          // Custom behaviour in the onbeforeSourceLoaded callback
          Event.SOURCE_LOADED,
        ),
        //#endregion STARTUP and STARTUP_AUTOPLAY

        on(Event.PLAYING).stayIn(State.PLAYING),
        on(Event.TIMECHANGED).stayIn(State.PLAYING),
        on(Event.END_BUFFERING).stayIn(State.PLAYING),
        {name: Event.START_BUFFERING, from: State.PLAYING, to: State.BUFFERING},
        {name: Event.END_BUFFERING, from: State.BUFFERING, to: State.PLAYING},
        on(Event.TIMECHANGED).stayIn(State.BUFFERING),

        {name: Event.PAUSE, from: State.PLAYING, to: State.PAUSE},
        {name: Event.PAUSE, from: State.BUFFERING, to: State.PAUSE},
        {name: Event.PLAY, from: State.PAUSE, to: State.PLAYING},

        {name: Event.VIDEO_CHANGE, from: State.PLAYING, to: State.QUALITY_CHANGE},
        {name: Event.AUDIO_CHANGE, from: State.PLAYING, to: State.QUALITY_CHANGE},
        {name: Event.FINISH_QUALITY_CHANGE, from: State.QUALITY_CHANGE, to: State.PLAYING},

        //#region Seeking

        // start seeking
        {name: Event.SEEK, from: State.PLAYING, to: State.PLAY_SEEKING},

        // mark the seeked as finished
        {name: Event.PLAY, from: State.PLAY_SEEKING, to: State.END_PLAY_SEEKING},
        {name: Event.PLAYING, from: State.PLAY_SEEKING, to: State.END_PLAY_SEEKING},
        {name: Event.SEEKED, from: State.PLAY_SEEKING, to: State.END_PLAY_SEEKING},

        // seek while previous seek not completed
        {name: Event.SEEK, from: State.END_PLAY_SEEKING, to: State.PLAY_SEEKING},

        // seek completely finished and cont playing
        {name: Event.PLAYING, from: State.END_PLAY_SEEKING, to: State.PLAYING},
        {name: Event.TIMECHANGED, from: State.END_PLAY_SEEKING, to: State.PLAYING},

        // if PAUSE happens during the seeking (or at the end of seeking) we need to transition to PAUSE state
        {name: Event.PAUSE, from: [State.PLAY_SEEKING, State.END_PLAY_SEEKING], to: State.PAUSE},

        //#region Seeking disabled transitions
        // ignore transitions in PLAY_SEEKING
        on(Event.SEEK).stayIn(State.PLAY_SEEKING),
        on(Event.START_BUFFERING).stayIn(State.PLAY_SEEKING),
        on(Event.END_BUFFERING).stayIn(State.PLAY_SEEKING),

        // ignore transitions in END_PLAY_SEEKING
        on(Event.START_BUFFERING).stayIn(State.END_PLAY_SEEKING),
        on(Event.END_BUFFERING).stayIn(State.END_PLAY_SEEKING),
        on(Event.SEEKED).stayIn(State.END_PLAY_SEEKING),
        on(Event.PLAY).stayIn(State.END_PLAY_SEEKING),

        // ignore Seek events during PAUSE
        on(Event.SEEK).stayIn(State.PAUSE),
        on(Event.SEEKED).stayIn(State.PAUSE),

        // ignore seeked during Playing because of not allowed keyboard seeking
        on(Event.SEEKED).stayIn(State.PLAYING),
        //#endregion Seeking disabled transitions

        //#endregion Seeking

        {name: Event.END, from: State.PLAY_SEEKING, to: State.END},
        {name: Event.END, from: State.PLAYING, to: State.END},
        {name: Event.END, from: State.PAUSE, to: State.END},

        on(Event.SEEK).stayIn(State.END),
        on(Event.SEEKED).stayIn(State.END),
        on(Event.TIMECHANGED).stayIn(State.END),
        on(Event.END_BUFFERING).stayIn(State.END),
        on(Event.START_BUFFERING).stayIn(State.END),
        on(Event.END).stayIn(State.END),

        {name: Event.PLAY, from: State.END, to: State.STARTUP},

        {name: Event.ERROR, from: this.getAllStatesBut([State.STARTUP, State.STARTUP_AUTOPLAY]), to: State.ERROR},

        {
          name: Event.UNLOAD,
          from: this.getAllStatesBut([State.STARTUP, State.STARTUP_AUTOPLAY]),
          to: State.END,
        },

        {name: Event.SUBTITLE_CHANGE, from: State.PLAYING, to: State.SUBTITLE_CHANGING},
        on(Event.SUBTITLE_CHANGE).stayIn(State.PAUSE),
        on(Event.SUBTITLE_CHANGE).stayIn(State.BUFFERING),
        on(Event.SUBTITLE_CHANGE).stayIn(State.END_PLAY_SEEKING),
        on(Event.SUBTITLE_CHANGE).stayIn(State.SUBTITLE_CHANGING),
        {name: Event.TIMECHANGED, from: State.SUBTITLE_CHANGING, to: State.PLAYING},

        {name: Event.AUDIOTRACK_CHANGED, from: State.PLAYING, to: State.AUDIOTRACK_CHANGING},
        on(Event.AUDIOTRACK_CHANGED).stayIn(State.PAUSE),
        on(Event.AUDIOTRACK_CHANGED).stayIn(State.BUFFERING),
        on(Event.AUDIOTRACK_CHANGED).stayIn(State.END_PLAY_SEEKING),
        on(Event.AUDIOTRACK_CHANGED).stayIn(State.AUDIOTRACK_CHANGING),
        {name: Event.TIMECHANGED, from: State.AUDIOTRACK_CHANGING, to: State.PLAYING},

        // #region Ads
        {name: Event.START_AD, from: [State.STARTUP, State.STARTUP_AUTOPLAY], to: State.AD_STARTUP},
        {name: Event.END_AD, from: State.AD_STARTUP, to: State.STARTUP},
        on(Event.PLAY).stayIn(State.AD_STARTUP),
        on(Event.PAUSE).stayIn(State.AD_STARTUP),

        {name: Event.START_AD, from: State.READY, to: State.AD_READY},
        /** After the pre-rolls, the main content will always start playing, no matter if autoplay: true/false */
        {name: Event.END_AD, from: State.AD_READY, to: State.STARTUP},
        on(Event.PLAY).stayIn(State.AD_READY),
        on(Event.PAUSE).stayIn(State.AD_READY),

        {name: Event.START_AD, from: State.PAUSE, to: State.AD_PAUSE},
        {name: Event.END_AD, from: State.AD_PAUSE, to: State.PAUSE},
        on(Event.PLAY).stayIn(State.AD_PAUSE),
        on(Event.PAUSE).stayIn(State.AD_PAUSE),

        // This handles the edge case where an ad starts while the audio track is being changed.
        // AUDIOTRACK_CHANGING can only be entered from PLAYING state, so we know playback was active.
        // Therefore, START_AD during AUDIOTRACK_CHANGING should transition to AD_PLAYING (same as START_AD from PLAYING).
        {name: Event.START_AD, from: State.AUDIOTRACK_CHANGING, to: State.AD_PLAYING},
        {name: Event.START_AD, from: State.PLAYING, to: State.AD_PLAYING},
        {name: Event.END_AD, from: State.AD_PLAYING, to: State.PLAYING},
        on(Event.PLAY).stayIn(State.AD_PLAYING),
        on(Event.PAUSE).stayIn(State.AD_PLAYING),
        {name: Event.END, from: State.AD_PLAYING, to: State.END},

        // Ignore timechanged events during ads
        on(Event.TIMECHANGED).stayIn(State.AD_STARTUP),
        on(Event.TIMECHANGED).stayIn(State.AD_READY),
        on(Event.TIMECHANGED).stayIn(State.AD_PAUSE),
        on(Event.TIMECHANGED).stayIn(State.AD_PLAYING),

        // Ignore End Ad events when no ad has started
        on(Event.END_AD).stayIn(State.PLAYING),
        on(Event.END_AD).stayIn(State.PAUSE),
        on(Event.END_AD).stayIn(State.READY),
        on(Event.END_AD).stayIn(State.STARTUP),
        // #endregion

        {name: Event.MUTE, from: State.READY, to: State.MUTING_READY},
        {name: Event.UN_MUTE, from: State.READY, to: State.MUTING_READY},
        {name: 'FINISH_MUTING', from: State.MUTING_READY, to: State.READY},

        {name: Event.MUTE, from: State.PLAYING, to: State.MUTING_PLAY},
        {name: Event.UN_MUTE, from: State.PLAYING, to: State.MUTING_PLAY},
        {name: 'FINISH_MUTING', from: State.MUTING_PLAY, to: State.PLAYING},

        {name: Event.MUTE, from: State.PAUSE, to: State.MUTING_PAUSE},
        {name: Event.UN_MUTE, from: State.PAUSE, to: State.MUTING_PAUSE},
        {name: 'FINISH_MUTING', from: State.MUTING_PAUSE, to: State.PAUSE},

        {name: Event.START_CAST, from: [State.READY, State.PAUSE, State.PLAYING], to: State.CASTING},
        on(Event.PAUSE).stayIn(State.CASTING),
        on(Event.PLAY).stayIn(State.CASTING),
        on(Event.PLAYING).stayIn(State.CASTING),
        on(Event.TIMECHANGED).stayIn(State.CASTING),
        on(Event.MUTE).stayIn(State.CASTING),
        on(Event.UN_MUTE).stayIn(State.CASTING),
        on(Event.SEEK).stayIn(State.CASTING),
        on(Event.SEEKED).stayIn(State.CASTING),
        {name: Event.END_CAST, from: State.CASTING, to: State.READY},

        on(Event.SEEK).stayIn(State.READY),
        on(Event.SEEKED).stayIn(State.READY),
        on(Event.SEEKED).stayIn(State.STARTUP),

        {
          name: Event.MANUAL_SOURCE_CHANGE,
          from: this.getAllStatesBut([State.SETUP, State.STARTUP, State.STARTUP_AUTOPLAY]),
          to: State.SOURCE_CHANGING,
        },
        {name: Event.MANUAL_SOURCE_CHANGE, from: State.SETUP, to: State.INITIAL_SOURCE_CHANGE},
        {
          name: Event.SOURCE_UNLOADED,
          from: this.getAllStatesBut([State.STARTUP, State.STARTUP_AUTOPLAY]),
          to: State.SOURCE_CHANGING,
        },

        // {name: Event.READY, from: State.SOURCE_CHANGING, to: State.READY},

        // Because of this state transition, we are able to make "a cut" in the current PLAYING / PAUSE state
        // and send "playing" / "pause" sample with the current custom data values. The new changed custom data values
        // will be set on upcoming samples after we transition back from the CUSTOMDATACHANGE state.
        // For any other states, we don't care, because InternalAdapter is not emitting the event.
        {name: Event.CUSTOM_DATA_CHANGE, from: [State.PLAYING, State.PAUSE], to: State.CUSTOMDATACHANGE},
        {name: Event.PLAYING, from: State.CUSTOMDATACHANGE, to: State.PLAYING},
        {name: Event.PAUSE, from: State.CUSTOMDATACHANGE, to: State.PAUSE},
      ],

      // Keep callbacks sorted by order in which they are called by JSM library (and after order, sorted by name)
      //
      // Do not use convenience, shortened callbacks
      //  - instead of `onEVENT` use `onafterEVENT`
      //  - instead of `onSTATE` use `onenterSTATE`
      //
      // see: https://github.com/jakesgordon/javascript-state-machine/blob/2.4.0/README.md#callbacks
      callbacks: {
        [`onbefore${Event.SOURCE_LOADED}`]: (
          _event,
          from,
          _to,
          _eventTimestamp: number,
          _eventData: AnalyticsEventBase,
        ) => {
          if (from === State.STARTUP_AUTOPLAY) {
            this.sourceLoadedDuringAutoplayStartup = true;
          }
        },

        [`onbefore${Event.SOURCE_UNLOADED}`]: (
          _event,
          from,
          _to,
          _eventTimestamp: number,
          _eventData: AnalyticsEventBase,
        ) => {
          // Prevent SOURCE_UNLOADED from triggering VIDEOSTART_FAILED during STARTUP_AUTOPLAY
          // if the new source hasn't been loaded yet.
          //
          // During source changes with autoplay, SOURCE_UNLOADED (cleanup of previous source)
          // may arrive AFTER player.load() is called but BEFORE SOURCE_LOADED (new source).
          // We ignore this cleanup event and only treat SOURCE_UNLOADED as an error
          // once the new source has been successfully loaded.
          if (from === State.STARTUP_AUTOPLAY && !this.sourceLoadedDuringAutoplayStartup) {
            return false;
          }

          return true;
        },

        [`onbefore${Event.PAUSE}`]: (_event, from, _to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          // Handle PAUSE during STARTUP_AUTOPLAY with special transitions based on source load status.
          // See sourceLoadedDuringAutoplayStartup documentation for detailed explanation.
          if (from === State.STARTUP_AUTOPLAY) {
            if (this.sourceLoadedDuringAutoplayStartup) {
              // Source loaded: transition to READY (normal behavior)
              this.stateMachine[CustomEvents.PAUSE_AFTER_SOURCE_LOADED](eventTimestamp, eventData);
            } else {
              // Source not loaded: transition to INITIAL_SOURCE_CHANGE (preserve READY semantics)
              this.stateMachine[CustomEvents.PAUSE_BEFORE_SOURCE_LOADED](eventTimestamp, eventData);
            }
            return false;
          }

          return true;
        },

        [`onleave${State.END_PLAY_SEEKING}`]: (
          _event,
          _from,
          to,
          eventTimestamp: number,
          eventData: AnalyticsEventBase,
        ) => {
          if (to === State.PLAY_SEEKING) {
            // seeking is not finished yet, do not send "seeking" sample
            return;
          }

          // seeking has finished / was interrupted - send out seeking sample
          const seekDuration = eventTimestamp - (this.seekStartTimestamp ?? eventTimestamp);
          this.seekStartTimestamp = undefined;
          this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          this.stateMachineCallbacks.seeking(seekDuration);
        },

        [`onleave${State.PLAYING}`]: () => {
          this.playingHeartbeatService.stopHeartbeat();
        },

        [`onleave${State.STARTUP}`]: (_event, _from, to, _eventTimestamp: number, _eventData) => {
          if (to === State.PLAYING) {
            this.startupHasSucceeded = true;
          }
        },

        [`onleave${State.STARTUP_AUTOPLAY}`]: (_event, _from, to, _eventTimestamp: number, _eventData) => {
          this.sourceLoadedDuringAutoplayStartup = false;
          this.clearVideoStartTimeout();
          if (to === State.PLAYING) {
            this.startupHasSucceeded = true;
          }
        },

        [`onleave${State.PLAY_SEEKING}`]: (_event, _from, to, eventTimestamp: number, _eventData) => {
          if (to === State.END_PLAY_SEEKING) {
            // regular seek workflow preparing to end seeking
            return;
          }

          // seek interrupted - not regular exit of seeking
          const seekDuration = eventTimestamp - (this.seekStartTimestamp ?? eventTimestamp);
          this.seekStartTimestamp = undefined;
          this.stateMachineCallbacks.seeking(seekDuration);
        },

        onleavestate: (event, from, to, eventTimestamp, eventData: AnalyticsEventBase) => {
          logStateMachineTransition('LEAVE', event, from, to);

          // Skip this JSM callback for JSM initial event
          // This event is fired when the state machine is constructed,
          // to transition us from 'none' to custom initial 'SETUP' state
          if (event === JSM_INITIAL_EVENT) {
            return;
          }

          if (from === State.BUFFERING) {
            this.resetBufferingHelpers();
          }

          // Skip this JSM callback when an JSM event without a timestamp is called,
          // because it would cause NPE in analytics code (state duration calculation etc.).
          // This probably has happened because the state machine was not used correctly,
          // aka. JSM event was not called via `callEvent` method as it should be!
          if (!eventTimestamp) {
            return;
          }

          this.addStatesToLog(event, from, to, eventTimestamp, eventData);
          const stateDuration = eventTimestamp - this.onEnterStateTimestamp;

          if (eventData && to !== State.END_PLAY_SEEKING) {
            this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          }

          if (eventData && event !== Event.END_AD) {
            this.lastTrackedVideoEndObject = eventData;
          }

          if ((from === State.READY || from === State.AD_STARTUP) && to === State.STARTUP) {
            this.setVideoStartTimeout();
          } else if (from === State.STARTUP && (to === State.PLAYING || to === State.AD_STARTUP)) {
            this.clearVideoStartTimeout();
          }

          const lowerCaseFrom = String(from).toLowerCase();
          if (to === State.VIDEOSTART_FAILED) {
            this.clearVideoStartTimeout();
            this.stateMachineCallbacks.videoStartFailed(stateDuration, createVideoStartFailedReason(event));
          } else if (from === State.END_PLAY_SEEKING) {
            // NOOP - skip last else block to not send sample
            // sending of sample is handled in `on Leave Seeking` state transition handler
            noOp();
          } else if (
            from === State.AD_PAUSE ||
            from === State.AD_READY ||
            from === State.AD_PLAYING ||
            from === State.AD_STARTUP
          ) {
            if (event === Event.UNLOAD) {
              this.stateMachineCallbacks.closedAd(stateDuration);
            } else {
              if (isNotNullish(this.lastTrackedVideoEndObject)) {
                this.stateMachineCallbacks.setVideoTimeStartFromEvent(this.lastTrackedVideoEndObject);
                this.stateMachineCallbacks.setVideoTimeEndFromEvent(this.lastTrackedVideoEndObject);
              }
              this.stateMachineCallbacks.ad(stateDuration, 'ad');
            }
          } else if (event === Event.UNLOAD) {
            if (from === State.PLAYING) {
              this.stateMachineCallbacks.closedPlaying(stateDuration);
            } else {
              // we are only interested in last sample when in playing state for unload events
              return;
            }
          } else if (event === Event.START_AD && (from === State.STARTUP || from === State.STARTUP_AUTOPLAY)) {
            // an ad starting during (autoplay) startup must not emit a startup sample yet - it is sent later when
            // content playback actually starts (leaving STARTUP after the ad)
            noOp();
          } else if (event === Event.SOURCE_LOADED && from === State.INITIAL_SOURCE_CHANGE) {
            noOp();
          } else if (from === State.SETUP) {
            this.stateMachineCallbacks.setup();
          } else if (
            (event === Event.PAUSE && from === State.STARTUP && to === State.READY) ||
            (event === CustomEvents.PAUSE_BEFORE_SOURCE_LOADED &&
              from === State.STARTUP_AUTOPLAY &&
              to === State.INITIAL_SOURCE_CHANGE) ||
            (event === CustomEvents.PAUSE_AFTER_SOURCE_LOADED && from === State.STARTUP_AUTOPLAY && to === State.READY)
          ) {
            // fired PAUSE event in STARTUP state or STARTUP_AUTOPLAY state indicates we are moving back to READY/INITIAL_SOURCE_CHANGE,
            // so we have to clean setTimeout, and we should not call any stateMachineCallbacks
            this.clearVideoStartTimeout();
          } else if (from === State.STARTUP_AUTOPLAY) {
            // send normal startup sample when leaving STARTUP_AUTOPLAY state
            this.stateMachineCallbacks.startup(stateDuration, State.STARTUP.toLowerCase());
          } else if (from !== State.ERROR) {
            // Default "onleavestate" state machine callback
            //
            // Every time, when we leave a state, we should try to call "state" callback,
            // to be able to make "a cut" and send analytics sample (if necessary)
            const defaultStateMachineCallbackFn = this.stateMachineCallbacks[lowerCaseFrom];
            if (isFunction(defaultStateMachineCallbackFn)) {
              // a try-catch block to add more resilience against callback errors
              try {
                defaultStateMachineCallbackFn(stateDuration, lowerCaseFrom, eventData);
              } catch (e) {
                logger.error('Exception occurred in State Machine callback ' + lowerCaseFrom, eventData, e);
              }
            } else {
              // if callback is not defined, log a warning to indicate to the developer that
              //  - a new callback function needs to be implemented to relay on this default state machine callback
              //  - or if no callback is needed, we should
              //    - early return from this JSM callback
              //    - or add this state to the ignored list
              logMissingCallbackWarning(from, [State.READY, State.SOURCE_CHANGING]);
            }
          }

          if (
            eventData &&
            to !== State.END_PLAY_SEEKING &&
            from !== State.PLAY_SEEKING &&
            to !== State.PLAY_SEEKING &&
            from !== State.AD_STARTUP &&
            from !== State.AD_READY &&
            from !== State.AD_PLAYING &&
            from !== State.AD_PAUSE
          ) {
            this.stateMachineCallbacks.setVideoTimeStartFromEvent(eventData);
          }

          if (event === Event.MUTE) {
            this.stateMachineCallbacks.mute();
          } else if (event === Event.UN_MUTE) {
            this.stateMachineCallbacks.unMute();
          }
        },

        [`onenter${State.STARTUP_AUTOPLAY}`]: () => {
          this.setVideoStartTimeout();
        },

        [`onenter${State.SOURCE_CHANGING}`]: () => {
          this.startupHasSucceeded = false;
        },

        [`onenter${State.PLAYING}`]: () => {
          this.playingHeartbeatService.startHeartbeat();
        },

        [`onenter${State.PLAY_SEEKING}`]: (event, from, _to, eventTimestamp: number, _eventData) => {
          if (from === State.END_PLAY_SEEKING) {
            // no start of seeking -> continues to seek and we don't need to have seek start timestamp
            return;
          }

          this.seekStartTimestamp = eventTimestamp;
        },

        [`onenter${State.QUALITY_CHANGE}`]: (_event, _from, _to, eventTimestamp, eventData) => {
          // Quality change should produce a "signal sample" and immediately transition us back to PLAYING state
          this.stateMachineCallbacks.qualitychange(eventData);
          this.stateMachine[Event.FINISH_QUALITY_CHANGE](eventTimestamp);
        },

        onenterstate: (event, from, to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          logStateMachineTransition('ENTER', event, from, to);

          this.onEnterStateTimestamp = eventTimestamp ?? Date.now();

          if (
            eventData &&
            to !== State.END_PLAY_SEEKING &&
            from !== State.PLAY_SEEKING &&
            from !== State.AD_STARTUP &&
            from !== State.AD_READY &&
            from !== State.AD_PLAYING &&
            from !== State.AD_PAUSE &&
            !(event === Event.SEEK && from === State.END_PLAY_SEEKING)
          ) {
            this.stateMachineCallbacks.setVideoTimeStartFromEvent(eventData);
          }

          if (event === Event.START_CAST && to === State.CASTING) {
            this.stateMachineCallbacks.startCasting(eventTimestamp, eventData);
          }

          if (to === State.BUFFERING) {
            this.startBufferingHeartbeatInterval();
          }
        },

        [`onafter${Event.ERROR}`]: (_event, _from, _to, _eventTimestamp, eventData: ErrorEvent) => {
          this.stateMachineCallbacks.error(eventData);
        },

        [`onafter${Event.SOURCE_UNLOADED}`]: (
          _event,
          _from,
          _to,
          _eventTimestamp: number,
          _eventData: AnalyticsEventBase,
        ) => {
          // Reset the flag after SOURCE_UNLOADED is processed.
          // This prepares the state machine for the next source load/change cycle.
          this.sourceLoadedDuringAutoplayStartup = false;
        },

        onafterevent: (event, from, to, eventTimestamp: number, eventData) => {
          if (event === Event.MANUAL_SOURCE_CHANGE && from === State.SETUP) {
            this.stateMachineCallbacks.initialSourceChange(eventData);
          } else if (event === Event.MANUAL_SOURCE_CHANGE) {
            this.stateMachineCallbacks.manualSourceChange(eventData);
          }

          if (to === State.MUTING_READY || to === State.MUTING_PLAY || to === State.MUTING_PAUSE) {
            this.stateMachine.FINISH_MUTING(eventTimestamp);
          }
        },
      },
    });
  }

  getStates() {
    return this.debuggingStates;
  }

  setEnabledDebugging(enabled: boolean) {
    this.enabledDebugging = enabled;
  }

  private addStatesToLog(
    event: string | undefined,
    from: string | undefined,
    to: string | undefined,
    timestamp: number,
    eventObject: any,
  ) {
    if (this.enabledDebugging) {
      this.debuggingStates.push(new EventDebugging(event, from, to, timestamp, eventObject));
    }
  }
}
